import { useEffect, useMemo, useState } from 'react'

import { traceService } from '../services/traceService'

export type ObservabilityFunnel = {
  windowMinutes?: number
  // Backward/forward compatible with backend: either record or array entries.
  stageCounts?:
    | Record<string, number>
    | Array<{ stage: string; count: number; dropoffRate?: number; transitionsOut?: number; transitionsIn?: number }>
  stageDropoff?: Record<string, number>
  checkoutPromotions?: number
  funnelOverridesApplied?: number
  funnelOverridesBlocked?: number
  avgCheckoutMomentum?: number
  avgClosingReadinessIndex?: number
  // Optional. If absent, we compute it from `authoritySamples` when available.
  authorityModeBreakdown?: Record<string, number>
  authoritySamples?: Array<{ funnelAuthorityMode?: string | null }>
}

function fmt(v: unknown) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '-'
  return Math.round(n * 100) / 100
}

export function ObservabilityFunnelView({
  tenantId,
  funnel,
  loading,
  error,
  onRefresh,
}: {
  tenantId: string
  funnel: ObservabilityFunnel | null
  loading: boolean
  error: string
  onRefresh: () => void
}) {
  const stageCountsRows = useMemo(() => {
    const raw = funnel?.stageCounts
    if (!raw) return []

    if (Array.isArray(raw)) {
      return raw
        .filter((row) => Boolean(row?.stage))
        .map((row) => [String(row.stage), Number(row.count || 0)] as const)
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    }

    return Object.entries(raw || {})
      .filter(([k]) => Boolean(k))
      .map(([k, v]) => [k, Number(v || 0)] as const)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
  }, [funnel])

  const dropoffRows = useMemo(() => {
    const raw = funnel?.stageCounts
    if (!raw || !Array.isArray(raw)) return []
    // Derive drop-off from stageCounts array if present.
    return raw
      .filter((row) => Boolean(row?.stage))
      .map((row) => [String(row.stage), Number(row.dropoffRate ?? 0)] as const)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
  }, [funnel])

  const authorityRows = useMemo(() => {
    const explicit = funnel?.authorityModeBreakdown
    if (explicit && typeof explicit === 'object' && !Array.isArray(explicit)) {
      return Object.entries(explicit)
        .filter(([k]) => Boolean(k))
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    }

    const samples = Array.isArray(funnel?.authoritySamples) ? funnel?.authoritySamples : []
    const counts = samples.reduce((acc, sample) => {
      const key = String(sample?.funnelAuthorityMode ?? '').trim()
      if (!key) return acc
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    }, {} as Record<string, number>)
    return Object.entries(counts).sort((a, b) => (b[1] || 0) - (a[1] || 0))
  }, [funnel])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Funnel Health</div>
          <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.65)' }}>
            Progresion, drop-off y overrides, sin confundir advisory/shadow/hard-lock.
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={!tenantId || loading}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.04)',
            color: '#e2e8f0',
            fontSize: 12,
            fontWeight: 800,
            cursor: !tenantId || loading ? 'not-allowed' : 'pointer',
            opacity: !tenantId || loading ? 0.6 : 1,
          }}
        >
          Refresh
        </button>
      </div>

      {!tenantId ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          Selecciona un tenant para ver funnel health.
        </div>
      ) : loading ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          Cargando funnel health...
        </div>
      ) : error ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.08)', color: '#fecaca' }}>
          {error}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Checkout promotions</div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{fmt(funnel?.checkoutPromotions)}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Overrides applied</div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{fmt(funnel?.funnelOverridesApplied)}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Overrides blocked</div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{fmt(funnel?.funnelOverridesBlocked)}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Avg closing readiness</div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{fmt(funnel?.avgClosingReadinessIndex)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 12 }}>
            <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 900, fontSize: 12 }}>
                Stage counts
              </div>
              {stageCountsRows.length === 0 ? (
                <div style={{ padding: 12, color: 'rgba(226,232,240,0.55)' }}>Sin datos.</div>
              ) : (
                stageCountsRows.map(([stage, count]) => (
                  <div key={stage} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{stage}</div>
                    <div style={{ fontSize: 12, fontWeight: 900 }}>{count}</div>
                  </div>
                ))
              )}
            </div>

            <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 900, fontSize: 12 }}>
                Stage drop-off
              </div>
              {dropoffRows.length === 0 ? (
                <div style={{ padding: 12, color: 'rgba(226,232,240,0.55)' }}>Sin datos.</div>
              ) : (
                dropoffRows.map(([stage, count]) => (
                  <div key={stage} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{stage}</div>
                    <div style={{ fontSize: 12, fontWeight: 900 }}>{`${Math.round((Number(count) || 0) * 1000) / 10}%`}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', overflow: 'hidden', marginTop: 12 }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 900, fontSize: 12 }}>
              Funnel authority mode (conteo)
            </div>
            {authorityRows.length === 0 ? (
              <div style={{ padding: 12, color: 'rgba(226,232,240,0.55)' }}>Sin datos.</div>
            ) : (
              authorityRows.map(([mode, count]) => (
                <div key={mode} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{mode}</div>
                  <div style={{ fontSize: 12, fontWeight: 900 }}>{count}</div>
                </div>
              ))
            )}
          </div>

          <details style={{ marginTop: 12 }}>
            <summary style={{ fontSize: 11, color: 'rgba(226,232,240,0.65)', cursor: 'pointer' }}>Ver contrato (campos)</summary>
            <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 11, color: '#a7f3d0', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 12, padding: 12 }}>
{JSON.stringify(funnel, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  )
}

export default function DebugObservabilityFunnel({
  tenantId,
  minutes = 1440,
}: {
  tenantId: string
  minutes?: number
}) {
  const [funnel, setFunnel] = useState<ObservabilityFunnel | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const refresh = async () => {
    if (!tenantId) return
    setLoading(true)
    setError('')
    try {
      const res = await traceService.getObservabilityFunnel({ tenantId, minutes })
      setFunnel(res.data ?? null)
    } catch {
      setFunnel(null)
      setError('No se pudo cargar el funnel health')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [tenantId, minutes])

  return (
    <ObservabilityFunnelView
      tenantId={tenantId}
      funnel={funnel}
      loading={loading}
      error={error}
      onRefresh={refresh}
    />
  )
}
